import React, { useState, useEffect } from 'react';
import { stripEmoji, shortLabel } from '../utils/text';

// Section éditoriale : un titre en petites capitales, une règle, du contenu.
const Section = ({ title, aside, children }) => (
  <section className="pt-8 border-t border-sand-200">
    <div className="flex items-baseline justify-between gap-4 mb-5">
      <h2 className="label-caps">{title}</h2>
      {aside && <span className="label-caps text-ink-400">{aside}</span>}
    </div>
    {children}
  </section>
);

// Ligne « libellé — jauge — valeur », le motif de base de cette page.
const BarRow = ({ label, value, max, accent = 'bg-ink-700' }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-4 py-2.5">
      <span className="flex-1 min-w-0 truncate text-sm text-ink-700">{label}</span>
      <div className="w-24 sm:w-40 h-[3px] bg-sand-200 rounded-full overflow-hidden shrink-0">
        <div className={`h-full rounded-full ${accent}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-10 text-right text-sm font-medium text-ink-900 tabular-nums shrink-0">
        {value}
      </span>
    </div>
  );
};

const StatsPage = () => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await fetch('https://n8n.automationtekleabsam.com/webhook/load-stats');
      const data = await response.json();

      console.log('📊 Stats reçues du webhook:', data);
      console.log('- success:', data.success);
      console.log('- stats:', data.stats);
      console.log('- last7Days:', data.stats?.last7Days);

      if (data.success && data.stats) {
        setStats(data.stats);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pb-28 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 mx-auto mb-4 rounded-full border-2 border-sand-300 border-t-clay-500 animate-spin" />
          <div className="label-caps">Chargement des statistiques</div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen pb-28 flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <h2 className="font-display text-2xl text-ink-900 mb-3">
            Rien à mesurer
          </h2>
          <p className="text-ink-500 leading-relaxed">
            Les statistiques apparaîtront après vos premières révisions.
          </p>
        </div>
      </div>
    );
  }

  const totalCards = stats.totalCards || 0;
  const dailyCounts = (stats.last7Days || []).map((d) => d.count);
  const maxDaily = Math.max(...dailyCounts, 0);
  const chartScale = Math.max(maxDaily, 1); // évite la division par zéro

  const headline = [
    { value: totalCards, label: 'Cartes' },
    { value: stats.reviewsToday || 0, label: "Révisions aujourd'hui" },
    { value: `${stats.averageSuccessRate || 0}%`, label: 'Réussite' },
    { value: stats.streak || 0, label: 'Jours de série' },
  ];

  const details = [
    ['Total révisions', stats.totalReviews || 0],
    ['Moyenne par jour', stats.averagePerDay || 0],
    ['Cartes maîtrisées', stats.masteredCards || 0],
    ['En apprentissage', stats.learningCards || 0],
    ['Intervalle moyen', `${stats.averageInterval || 0} j`],
    ['Meilleure série', `${stats.bestStreak || 0} jours`],
  ];

  return (
    <div className="min-h-screen pb-28">
      <header className="max-w-2xl mx-auto px-5 pt-10 pb-8">
        <h1 className="font-display text-[2rem] leading-none text-ink-900">
          Statistiques
        </h1>
        <p className="label-caps mt-2">Votre progression</p>
      </header>

      <main className="max-w-2xl mx-auto px-5">
        {/* Les quatre chiffres de tête */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-6 pb-8">
          {headline.map((item) => (
            <div key={item.label}>
              <div className="font-display text-[2rem] leading-none text-ink-900 tabular-nums">
                {item.value}
              </div>
              <div className="label-caps mt-1.5">{item.label}</div>
            </div>
          ))}
        </div>

        {/* Activité récente */}
        <Section title="7 derniers jours" aside={`${maxDaily} max`}>
          {stats.last7Days && stats.last7Days.length > 0 && maxDaily > 0 ? (
            <div className="h-36 flex items-end gap-2">
              {stats.last7Days.map((day, i) => (
                <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                  <div className="relative group w-full flex-1 flex flex-col justify-end">
                    <div
                      className="w-full bg-clay-400 hover:bg-clay-500 rounded-t-sm transition-colors"
                      style={{ height: `${Math.max((day.count / chartScale) * 100, 3)}%` }}
                    />
                    <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-ink-900 text-onAccent
                                    text-[11px] rounded-md py-1 px-2 opacity-0 group-hover:opacity-100
                                    transition-opacity whitespace-nowrap pointer-events-none">
                      {day.count} révision{day.count > 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className="label-caps mt-2 text-[10px]">{day.label}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-ink-500 py-6">
              Aucune révision sur les sept derniers jours.
            </p>
          )}
        </Section>

        {/* Progression par deck */}
        {stats.deckProgress && stats.deckProgress.length > 0 && (
          <Section title="Progression par deck" aside="maîtrise">
            <div className="divide-y divide-sand-200">
              {stats.deckProgress.map((deck, i) => (
                <div key={i} className="py-4">
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="font-medium text-ink-900 truncate">
                      {stripEmoji(deck.name)}
                    </span>
                    <span className="label-caps tabular-nums shrink-0">
                      {deck.masteredCards}/{deck.totalCards} · {deck.masteryRate}%
                    </span>
                  </div>
                  <div className="mt-2.5 h-[3px] bg-sand-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-ink-700 rounded-full transition-all duration-500"
                      style={{ width: `${deck.masteryRate}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Répartition */}
        <Section title="Répartition des cartes">
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
            <div>
              <h3 className="label-caps text-ink-400 mb-1">Par niveau</h3>
              <div className="divide-y divide-sand-200">
                {stats.cardsByLevel &&
                  Object.entries(stats.cardsByLevel).map(([level, count]) => (
                    <BarRow
                      key={level}
                      label={shortLabel(level)}
                      value={count}
                      max={totalCards}
                    />
                  ))}
              </div>
            </div>

            <div>
              <h3 className="label-caps text-ink-400 mb-1">Par état</h3>
              <div className="divide-y divide-sand-200">
                {stats.cardsByState &&
                  Object.entries(stats.cardsByState).map(([state, count]) => (
                    <BarRow
                      key={state}
                      label={shortLabel(state)}
                      value={count}
                      max={totalCards}
                      accent="bg-clay-400"
                    />
                  ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Calendrier du mois */}
        {stats.monthlyCalendar && stats.monthlyCalendar.length > 0 && (
          <Section title="Calendrier du mois">
            <div className="grid grid-cols-7 gap-1.5">
              {stats.monthlyCalendar.map((day, i) => (
                <div
                  key={i}
                  className={`aspect-square rounded-md flex items-center justify-center
                              text-[11px] tabular-nums relative group ${
                    day.count === 0
                      ? 'bg-sand-100 text-ink-400'
                      : day.count < 5
                      ? 'bg-clay-100 text-clay-700'
                      : day.count < 10
                      ? 'bg-clay-200 text-clay-700'
                      : 'bg-clay-500 text-onAccent'
                  }`}
                >
                  {day.date}
                  {day.count > 0 && (
                    <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-ink-900
                                    text-onAccent text-[11px] rounded-md py-1 px-2 opacity-0
                                    group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                      {day.count} révision{day.count > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-1.5 mt-4">
              <span className="label-caps">Moins</span>
              <span className="w-3 h-3 rounded-sm bg-sand-100" />
              <span className="w-3 h-3 rounded-sm bg-clay-100" />
              <span className="w-3 h-3 rounded-sm bg-clay-200" />
              <span className="w-3 h-3 rounded-sm bg-clay-500" />
              <span className="label-caps">Plus</span>
            </div>
          </Section>
        )}

        {/* Détails */}
        <Section title="Détails">
          <dl className="divide-y divide-sand-200">
            {details.map(([label, value]) => (
              <div key={label} className="flex items-baseline justify-between gap-4 py-3">
                <dt className="text-sm text-ink-500">{label}</dt>
                <dd className="text-sm font-medium text-ink-900 tabular-nums">{value}</dd>
              </div>
            ))}
          </dl>
        </Section>
      </main>
    </div>
  );
};

export default StatsPage;
